import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router";
import {
  Button,
  FormField,
  Heading,
  Input,
  Text,
  Spinner,
  Alert,
  Stack,
} from "@/components/ds";
import { AuthLayout } from "@/components/auth/AuthLayout";
import styles from "./InviteAccept.module.css";

interface InviteInfo {
  readonly name: string | null;
  readonly email: string | null;
  readonly orgName: string;
  readonly isOwner: boolean;
  readonly isCleaner: boolean;
}

type PageState =
  | { readonly type: "loading" }
  | { readonly type: "invalid" }
  | { readonly type: "new_user"; readonly invite: InviteInfo }
  | { readonly type: "existing_user_logged_in"; readonly invite: InviteInfo }
  | { readonly type: "existing_user_not_logged_in"; readonly invite: InviteInfo }
  | { readonly type: "wrong_account"; readonly invite: InviteInfo; readonly loggedInEmail: string };

interface InviteAcceptProps {
  readonly token: string;
}

export function InviteAccept({ token }: InviteAcceptProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ type: "loading" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const response = await fetch(`/api/invites/${token}`);
        if (!response.ok) {
          setState({ type: "invalid" });
          return;
        }

        const data = await response.json();
        if (!data.success) {
          setState({ type: "invalid" });
          return;
        }

        const invite = data.data.invite as InviteInfo;
        if (invite.name) setName(invite.name);

        const isOpenLink = !invite.email;

        const meResponse = await fetch("/api/auth/me");
        const loggedIn = meResponse.ok;
        const meData = loggedIn ? await meResponse.json() : null;
        const loggedInEmail = meData?.data?.user?.email as string | undefined;

        if (isOpenLink) {
          if (loggedIn) {
            setState({ type: "existing_user_logged_in", invite });
          } else {
            setState({ type: "new_user", invite });
          }
        } else {
          if (loggedIn && loggedInEmail !== invite.email) {
            setState({ type: "wrong_account", invite, loggedInEmail: loggedInEmail ?? "" });
          } else if (loggedIn && loggedInEmail === invite.email) {
            setState({ type: "existing_user_logged_in", invite });
          } else if (data.data.existingUser) {
            setState({ type: "existing_user_not_logged_in", invite });
          } else {
            setState({ type: "new_user", invite });
          }
        }
      } catch {
        setState({ type: "invalid" });
      }
    }

    load();
  }, [token]);

  async function handleNewUserSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const invite = (state as { invite: InviteInfo }).invite;
      const isOpenLink = !invite.email;

      const body: Record<string, string> = { name, password };
      if (isOpenLink) {
        body.email = email;
      }

      const response = await fetch(`/api/invites/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!data.success) {
        setError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      navigate(data.data.redirectTo);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOutAndContinue() {
    setSubmitting(true);
    try {
      await fetch("/api/auth/logout", { method: "POST" });
      window.location.reload();
    } catch {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  async function handleExistingUserJoin() {
    setError("");
    setSubmitting(true);

    try {
      const response = await fetch(`/api/invites/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      const data = await response.json();
      if (!data.success) {
        setError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      navigate(data.data.redirectTo);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (state.type === "loading") {
    return (
      <AuthLayout>
        <div className={styles.center}>
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  if (state.type === "invalid") {
    return (
      <AuthLayout>
        <Stack gap={5} align="center">
          <Heading level={1} size="h3">Invalid invite link</Heading>
          <Text color="secondary">
            This invite link is no longer valid. It may have already been used or cancelled.
            Ask your team admin to send a new one.
          </Text>
          <Button href="/login">Go to login</Button>
        </Stack>
      </AuthLayout>
    );
  }

  const roleName = state.invite.isOwner && state.invite.isCleaner
    ? "a manager and cleaner"
    : state.invite.isOwner
      ? "a manager"
      : "a cleaner";

  const isOpenLink = !state.invite.email;

  if (state.type === "new_user") {
    return (
      <AuthLayout>
        <form onSubmit={handleNewUserSubmit} className={styles.form}>
          <Stack gap={2}>
            <Heading level={1} size="h3">Join {state.invite.orgName}</Heading>
            <Text color="secondary" size="sm">
              You&apos;ve been invited as {roleName}
            </Text>
          </Stack>

          {error && <Alert variant="danger">{error}</Alert>}

          <FormField label="Name" htmlFor="accept-name">
            <Input
              id="accept-name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
          </FormField>

          <FormField label="Email" htmlFor="accept-email">
            <Input
              id="accept-email"
              name="email"
              type="email"
              value={isOpenLink ? email : (state.invite.email ?? "")}
              onChange={isOpenLink ? (e) => setEmail(e.target.value) : undefined}
              disabled={isOpenLink ? submitting : true}
              required
            />
          </FormField>

          <FormField label="Password" htmlFor="accept-password">
            <Input
              id="accept-password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
              placeholder="At least 8 characters"
            />
          </FormField>

          <Button type="submit" fullWidth disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : "Create account"}
          </Button>

          <p className={styles.footer}>
            Already have an account?{" "}
            <Link to={`/login?redirect=/invite/${token}`} className={styles.link}>
              Log in
            </Link>
          </p>
        </form>
      </AuthLayout>
    );
  }

  if (state.type === "existing_user_logged_in") {
    return (
      <AuthLayout>
        <Stack gap={5}>
          <Stack gap={2}>
            <Heading level={1} size="h3">Join {state.invite.orgName}</Heading>
            <Text color="secondary" size="sm">
              You&apos;ve been invited as {roleName}
            </Text>
          </Stack>

          {error && <Alert variant="danger">{error}</Alert>}

          <Button onClick={handleExistingUserJoin} fullWidth disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : "Join team"}
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  if (state.type === "wrong_account") {
    return (
      <AuthLayout>
        <Stack gap={5}>
          <Stack gap={2}>
            <Heading level={1} size="h3">Join {state.invite.orgName}</Heading>
            <Text color="secondary" size="sm">
              You&apos;ve been invited as {roleName}
            </Text>
          </Stack>

          <Text>
            This invite was sent to <strong>{state.invite.email}</strong>, but
            you&apos;re signed in as <strong>{state.loggedInEmail}</strong>.
            Sign out to continue.
          </Text>

          {error && <Alert variant="danger">{error}</Alert>}

          <Button onClick={handleSignOutAndContinue} fullWidth disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : "Sign out and continue"}
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack gap={5}>
        <Stack gap={2}>
          <Heading level={1} size="h3">Join {state.invite.orgName}</Heading>
          <Text color="secondary" size="sm">
            You&apos;ve been invited as {roleName}
          </Text>
        </Stack>

        <Text>
          You already have a TidyRota account. Log in to join this team.
        </Text>

        <Button href={`/login?redirect=/invite/${token}`} fullWidth>
          Log in to join
        </Button>
      </Stack>
    </AuthLayout>
  );
}
